import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TagManager from "react-gtm-module";
import { Document, Page, pdfjs } from "react-pdf";
import { PageCallback } from "react-pdf/dist/cjs/shared/types";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import styled from "styled-components";
import Swal from "sweetalert2";
import { JsonContext } from "../../../context/JsonContextData";
import { cancelSignature } from "../../../service/cancelSignature";
import { postConsent } from "../../../service/postConsent";
import { Fields, postDataToServer } from "../../../service/postSign";
import ArrowLeft from "../../../svg/ArrowLeft";
import ArrowRight from "../../../svg/ArrowRight";
import CheckIcon from "../../../svg/CheckIcon";
import {
  containerWidth,
  documentHeight,
  DocumentHeightTotalResponsive,
  documentWidth,
} from "../../../utils/measuresForResponsiveness";
import { LoadingText } from "../../Home/CustomPDFViewer/styled";
import LoadingSpinner from "../../LoadingSpinner";
import Input from "../Input";
import Modal from "../Modal";
import InitialModal from "../Modals/InitialModal";
import SuccessfullySignedModal from "../Modals/SuccessfullySignedModal";
import WarningModal from "../Modals/WarningModal";
import { LazyImage } from "./LazyImage";
import {
  Button,
  ButtonModal,
  ButtonNext,
  ButtonPrev,
  Container,
  ContainerButton,
  ContainerModalInputs,
  ContainerSvg,
  DocumentWrapper,
  TopWrapper,
  TopWrapperP,
  TopWrapperSpan,
  Wrapper,
  WrapperButton,
} from "./styled";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const StyledPage = styled(Page)`
  display: none;
  canvas {
    position: relative;
    display: block;
    margin: 0 auto;
    max-width: 100%;
    border: 1px solid #ddd;
  }
`;
export interface FieldsProps {
  id: string;
  page: number;
  x: number;
  y: number;
  y_relative: number;
  width: number;
  height: number;
  type: "initial" | "signature" | "text";
  email: string;
  text_value: string;
  text_label: string;
  color: string;
  checkbox_group: string;
  checkbox_name: string;
}

interface PdfViewerProps {
  pdfUrl: string;
  pdfName: string;
  requesterName: string;
  signatureImage: string;
  initialImage: string;
  originalFields: FieldsProps[];
  setShowButton: (value: boolean) => void;
  hasUserRegister: boolean | undefined;
  onVerificationSuccess: () => void;
  onPostSuccess: () => void;
}

export interface SignProps {
  markingData?: {
    x: number;
    y: number;
    width: number;
    height: number;
    type: string;
    text_value: string;
  };
}

let global: any = [];
let globalEvent: any = null;

const CustomPDFViewerSign: React.FC<PdfViewerProps> = ({
  pdfUrl,
  pdfName,
  requesterName,
  signatureImage,
  initialImage,
  originalFields,
  setShowButton,
  hasUserRegister = false,
  onVerificationSuccess,
  onPostSuccess,
}) => {
  const canvasRefs = useRef<HTMLCanvasElement[]>([]);
  const [numPages, setNumPages] = React.useState(0);
  const [base64Pdf, setBase64Pdf] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isModalOpenWarning, setIsModalOpenWarning] = useState(false);
  const [isModalOpenSigned, setIsModalOpenSigned] = useState(false);
  const [inputValues, setInputValues] = useState<string[]>([]);
  const [isLastInputFilled, setIsLastInputFilled] = useState(false);
  const [currentFocusedInputIndex, setCurrentFocusedInputIndex] =
    useState<number>(-1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeInputIndex, setActiveInputIndex] = useState(-1);
  const [modalInputValue, setModalInputValue] = useState("");
  const [modalLabelText, setModalLabelText] = useState("");
  const [error, setError] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>("Aguarde...");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAccepted, setIsAccepted] = useState<boolean>(false);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  const [newWrapperWidth, setNewWrapperWidth] = useState(0);

  const [isInitialModalOpen, setIsInitialModalOpen] = useState(false);

  const url = process.env.REACT_APP_ASSINANDO;

  const { jsonData } = useContext(JsonContext);

  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const inputs = document.querySelectorAll('input[type="text"]');
  const countInputs = originalFields.filter((field) => field.type === "text");
  const isFilled = isLastInputFilled || countInputs.length === 0;
  const fields = originalFields.sort((a, b) => {
    if (a.type < b.type) {
      return 1;
    } else if (a.type > b.type) {
      return -1;
    } else {
      return 0;
    }
  });
  useEffect(() => {
    const checkSessionStorage = () => {
      if (sessionStorage.getItem("infoModalClosed") === "true") {
        setIsAccepted(true);
        verifyShowEditButton();
      }
    };
    checkSessionStorage();
  }, []);

  useEffect(() => {
    if (isAccepted) {
      onVerificationSuccess();
    }
  }, [isAccepted, onVerificationSuccess]);

  const verifyShowEditButton = () => {
    if (!hasUserRegister) {
      setShowButton(true);
    }
  };

  useEffect(() => {
    if (!isInitialModalOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }
    const timeoutId = setTimeout(() => {
      setLoadingMessage("Falha ao renderizar o PDF!");
    }, 10000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isInitialModalOpen]);

  useEffect(() => {
    const wrapper = document.getElementById("svgContainer");

    const handleResize = () => {
      if (wrapper) {
        const width = wrapper.clientWidth;

        if (width !== wrapperWidth) {
          setWrapperWidth(width);
          setNewWrapperWidth(wrapperWidth);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [newWrapperWidth, wrapperWidth]);

  useEffect(() => {
    if (wrapperWidth > newWrapperWidth || wrapperWidth < newWrapperWidth) {
      window.location.reload();
    }
  }, [newWrapperWidth]);

  const closeModal = () => {
    setIsModalOpenWarning(false);
    setIsModalOpenSigned(false);
    setIsModalOpen(false);
  };

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      const renderedCanvasList = canvasRefs.current;
      setBase64Pdf(renderedCanvasList.map((element) => element.toDataURL()));
      canvasRefs.current = [];
      setNumPages(numPages);
      setCurrentPage(1);
      if (globalEvent) {
        clearTimeout(globalEvent);
      }
      global = [];
      globalEvent = null;
    },
    []
  );

  const onRenderPageSuccess = useCallback(
    (page: PageCallback) => {
      global[page._pageIndex] = page;
      if (globalEvent) {
        clearTimeout(globalEvent);
      }
      globalEvent = setTimeout(() => {
        onDocumentLoadSuccess({ numPages });
      }, 200);
    },
    [numPages, onDocumentLoadSuccess]
  );

  const renderPages = useMemo(() => {
    return Array.from({ length: numPages }, (_, i) => (
      <StyledPage
        key={`page_${i + 1}`}
        pageNumber={i + 1}
        renderTextLayer={false}
        // scale={numPages <= 20 ? 2 : 1.4}
        renderAnnotationLayer={false}
        onRenderSuccess={onRenderPageSuccess}
        canvasRef={(ref: HTMLCanvasElement | null) => {
          if (ref !== null) {
            canvasRefs.current[i] = ref;
          }
        }}
      />
    ));
  }, [numPages, onRenderPageSuccess]);

  const renderPdfImages = useMemo(() => {
    if (base64Pdf.length === numPages) {
      return base64Pdf.map((element: string, index: number) => (
        <LazyImage key={index} src={element} index={index} />
      ));
    }
    return null;
  }, [base64Pdf, numPages]);

  const handleMarkingClick = (index: number) => {
    setActiveInputIndex(index);
    setModalInputValue(inputValues[index] || "");
    const clickedField = fields[index];
    if (clickedField && clickedField.text_value) {
      setModalLabelText(clickedField.text_value);
    } else {
      setModalLabelText("");
    }

    setIsModalOpen(true);
  };

  const handleInputChange = (index: number, value: string) => {
    if (value === "") {
      return setError(true);
    }
    const updatedInputValues = [...inputValues];
    updatedInputValues[index] = value;
    setInputValues(updatedInputValues);
    setActiveInputIndex(-1);
    setModalInputValue("");
    setIsModalOpen(false);
    const filledInputsCount = updatedInputValues.filter(
      (value) => value.trim() !== ""
    ).length;

    const allInputsFilled =
      filledInputsCount ===
      fields.filter((field) => field.type === "text").length;

    setError(false);
    setIsLastInputFilled(allInputsFilled);
  };

  const CalculatePercentageX = (
    x: number,
    OriginalWidth: number,
    documentWidth: number
  ) => {
    const newValue = documentWidth * (x / OriginalWidth);
    return newValue;
  };

  const CalculatePercentageY = (
    y: number,
    OriginalHeight: number,
    documentHeight: number
  ) => {
    const newValue = documentHeight * (y / OriginalHeight);
    return newValue;
  };

  const renderMarking = (
    markingData: SignProps["markingData"],

    focusNextInput: (index?: number) => void,

    index: number
  ) => {
    if (!markingData) return null;

    const { x, y, width, height, text_value, type } = markingData;
    const OriginalWidth = 960;
    const OriginalHeight = 1357;
    const originalTotalHeight = numPages * 1358;
    const documentTotalHeight = DocumentHeightTotalResponsive(numPages);
    const newPositionX = CalculatePercentageX(x, OriginalWidth, documentWidth);
    const newPositionY = CalculatePercentageY(
      y,
      originalTotalHeight,
      documentTotalHeight
    );
    const newWidth = (width / OriginalWidth) * documentWidth;
    const newWidthString = `${newWidth}px`;
    const newHeight = (height / OriginalHeight) * documentHeight;
    const fontSizeHorizontal = Math.min(newWidth * 0.25, 40);
    const fontSizeVertical = Math.min(newHeight * 0.25, 40);
    const fontSize = Math.min(fontSizeHorizontal, fontSizeVertical);

    const handleChange = (index: number, value: string) => {
      const updatedInputValues = [...inputValues];
      updatedInputValues[index] = value;
      setInputValues(updatedInputValues);
      setCurrentFocusedInputIndex(index);

      const filledInputsCount = updatedInputValues.filter(
        (value) => typeof value === "string" && value.trim() !== ""
      ).length;
      const allInputsFilled =
        filledInputsCount ===
        fields.filter((field) => field.type === "text").length;
      setIsLastInputFilled(allInputsFilled);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        focusNextInput(index + 1);
      }
    };
    return (
      <g
        key={index}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        {type === "text" && containerWidth >= 768 ? (
          <foreignObject
            x={newPositionX}
            y={newPositionY}
            width={newWidth}
            height={newHeight + 10}
          >
            <Input
              ref={index === 0 ? firstInputRef : null}
              type="text"
              fontSize="12px"
              background={inputValues[index] ? "#72E27F50" : "#355B9450"}
              width={newWidthString}
              height={newHeight}
              value={inputValues[index] || ""}
              onChange={(event) => handleChange(index, event.target.value)}
              placeholder={text_value}
              onKeyDown={handleKeyDown}
            />
          </foreignObject>
        ) : (
          <>
            <rect
              x={newPositionX}
              y={newPositionY}
              width={newWidth}
              height={newHeight}
              fill={
                type === "text"
                  ? inputValues[index]
                    ? "#72E27F"
                    : "#d8161650"
                  : "transparent"
              }
              stroke={
                type === "text"
                  ? inputValues[index]
                    ? "#94ce52"
                    : "#f85858"
                  : "none"
              }
              strokeWidth={2}
              ry={4}
              onClick={() => {
                if (type === "text" && containerWidth < 768) {
                  handleMarkingClick(index);
                }
              }}
            />
            {type === "text" ? (
              <text
                x={
                  type === "text"
                    ? newPositionX + 5
                    : newPositionX + newWidth / 2
                }
                y={newPositionY + newHeight / 2}
                fill="#2c2c2c"
                fontWeight="bold"
                textAnchor={type === "text" ? "start" : "middle"}
                dominantBaseline="middle"
                fontSize={type === "text" ? fontSize * 1.8 : fontSize * 1.1}
              >
                {!inputValues[index] ? text_value : inputValues[index]}
              </text>
            ) : (
              <image
                x={newPositionX}
                y={newPositionY}
                style={{ width: newWidth, height: newHeight }}
                xlinkHref={type === "signature" ? signatureImage : initialImage} // Specify the path to the image
              />
            )}
          </>
        )}
      </g>
    );
  };

  const focusNextInput = () => {
    const currentInputIndex = Array.from(inputs).findIndex(
      (input) => (input as HTMLInputElement).value.trim() === ""
    );
    if (currentInputIndex !== -1) {
      const currentInput = inputs[currentInputIndex] as HTMLInputElement;
      currentInput.focus();
      currentInput.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else {
      if (currentFocusedInputIndex === -1) {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
          firstInputRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  };
  const focusPrevInput = () => {
    const prevInputIndex = Math.max(currentFocusedInputIndex - 1, 0);
    const prevInput = inputs[prevInputIndex] as HTMLInputElement;

    if (prevInput) {
      prevInput.focus();
      prevInput.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleAccepted = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: "Concordar",
        userProject: "Assinando",
        page: "sign",
      },
    });
    setIsInitialModalOpen(true);
  };
  const handleButtonClick = async () => {
    const allInputsFilledAndValid = inputValues.every((value, index) => {
      return value.trim() !== "" && !error;
    });
    TagManager.dataLayer({
      dataLayer: {
        event: "Assinar",
        dataLayer: {
          userProject: "Assinando",
          page: "Sign",
        },
      },
    });
    if (allInputsFilledAndValid) {
      try {
        const postData: Fields = { fields: [] };

        fields.forEach((field, index) => {
          if (field.type === "text" && inputValues[index]?.trim() !== "") {
            postData.fields.push({
              id: field.id,
              text_value: inputValues[index],
            });
          }
        });
        setIsLoading(true);
        const response = await postDataToServer(JSON.stringify(postData));
        if (response) {
          sessionStorage.clear();
          setIsLoading(false);
          setIsModalOpenSigned(true);
          onPostSuccess();
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("Erro na solicitação:", error);
      }
    } else {
      setIsLoading(false);
      setIsModalOpenWarning(true);
    }
  };
  const handleCancelSigns = async () => {
    Swal.fire({
      title: "Desistir da assinatura?",
      text: "Tem certeza que deseja desistir da assinatura?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Não",
      confirmButtonColor: "#d81616",
      cancelButtonColor: "#77EC84",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsLoading(true);
          const tokenComplete = sessionStorage.getItem("token_app");
          const token = tokenComplete?.replace("token_app=", "");
          const tokenString: string = token ?? "";
          const response = await cancelSignature(tokenString);
          if (response.document) {
            sessionStorage.clear();
            window.location.href = `${url}`;
          }
        } catch (error: any) {
          console.error("Erro ao enviar os dados Json:", error);
        }
      }
    });
  };

  const handleAcceptDocument = async () => {
    try {
      const response = await postConsent(jsonData);
      if (response.consent) {
        sessionStorage.setItem("infoModalClosed", "true");
        setIsAccepted(true);
        verifyShowEditButton();
        setIsInitialModalOpen(false);
      }
    } catch (error: any) {
      setIsInitialModalOpen(false);
      console.error("Erro ao enviar os dados Json:", error);
    }
  };
  const renderNoData = () => {
    return <LoadingText>{loadingMessage}</LoadingText>;
  };
  const renderLoader = () => {
    return <LoadingSpinner />;
  };

  return (
    <>
      {!sessionStorage.getItem("infoModalClosed") && (
        <InitialModal
          onClose={() => setIsInitialModalOpen(false)}
          onSend={handleAcceptDocument}
          numberInputs={countInputs.length}
          isOpen={isInitialModalOpen}
        />
      )}
      {isLoading && <LoadingSpinner />}
      <WarningModal isOpen={isModalOpenWarning} onClose={closeModal} />
      <SuccessfullySignedModal
        isOpen={isModalOpenSigned}
        onClose={closeModal}
      />

      <DocumentWrapper>
        <Document
          className="documentRef"
          file={pdfUrl}
          noData={renderNoData}
          loading={renderLoader}
          onLoadSuccess={onDocumentLoadSuccess}
          renderMode="canvas"
        >
          {base64Pdf.length === 0 && renderPages}
        </Document>
      </DocumentWrapper>
      <Container>
        <TopWrapper>
          <TopWrapperSpan isColumn={true}>
            <p>
              <b>Nome do requisitante:</b> {requesterName}
            </p>
            <p>
              <b>Documento:</b> {pdfName}
            </p>
          </TopWrapperSpan>
          <WrapperButton>
            <Button
              showButton
              backgroundColor="#355B94"
              hoverBackgroundColor="#32568c"
              onClick={handleCancelSigns}
              isFilled={true}
              style={{ width: "100px" }}
            >
              <ArrowLeft />
              {isAccepted ? "Desistir" : "Rejeitar"}
            </Button>
            {isAccepted ? (
              <Button
                showButton
                backgroundColor="#77EC84"
                hoverBackgroundColor="#72E27F"
                onClick={handleButtonClick}
                cursor={isFilled ? "pointer" : "not-allowed"}
                isFilled={isFilled}
                color="#2c2c2c"
                style={{ width: "100px", justifyContent: "end" }}
              >
                {isFilled ? "Assinar" : "Preencher"}
                {isFilled && <ArrowRight fill="#2c2c2c" />}
              </Button>
            ) : (
              <Button
                showButton
                backgroundColor="#77EC84"
                hoverBackgroundColor="#72E27F"
                color="#2c2c2c"
                onClick={handleAccepted}
                isFilled={true}
              >
                Concordar
                <ArrowRight fill="#2c2c2c" />
              </Button>
            )}
          </WrapperButton>
          <TopWrapperSpan>
            <TopWrapperP>Página: </TopWrapperP>
            {currentPage} de {numPages}
          </TopWrapperSpan>
        </TopWrapper>
        <ContainerSvg
          onScroll={(event: any) => {
            const position = (event.target as Element).scrollTop;
            const currentPage = Math.floor(position / documentHeight) + 1;
            setCurrentPage(currentPage);
          }}
        >
          {base64Pdf.length !== numPages ? (
            <LoadingSpinner />
          ) : (
            <Wrapper numPages={numPages} id="svgContainer">
              {renderPdfImages}
              {isAccepted &&
                fields.map((field, index) => {
                  if (field.x && field.y && field.width && field.height) {
                    const pageNumber = field.page;
                    const yRelative = field.y - (pageNumber - 1) * 4;
                    return renderMarking(
                      {
                        x: parseFloat(field.x.toString()),
                        y: parseFloat(yRelative.toString()),
                        width: parseFloat(field.width.toString()),
                        height: parseFloat(field.height.toString()),
                        type: field.type,
                        text_value: field.text_value,
                      },
                      () => focusNextInput(),
                      index
                    );
                  }
                  return null;
                })}
            </Wrapper>
          )}
        </ContainerSvg>
        {isAccepted && countInputs.length > 0 && (
          <ContainerButton>
            <ButtonPrev onClick={focusPrevInput}>
              <ArrowLeft />
              Anterior
            </ButtonPrev>
            <ButtonNext
              onClick={focusNextInput}
              cursor={isLastInputFilled ? "not-allowed" : "pointer"}
            >
              {isLastInputFilled ? "Preenchido" : "Próximo"}
              {isLastInputFilled ? <CheckIcon /> : <ArrowRight />}
            </ButtonNext>
          </ContainerButton>
        )}
      </Container>
      <Modal isOpen={isModalOpen}>
        <ContainerModalInputs>
          <label style={{ marginBottom: "6px" }} htmlFor="">
            {modalLabelText}
          </label>
          <Input
            width="100%"
            height={30}
            type="text"
            fontSize="14px"
            background="#fff"
            value={modalInputValue}
            onChange={(event) => {
              setModalInputValue(event.target.value);
              setError(event.target.value.trim() === "");
            }}
            placeholder={modalLabelText}
          />
          {error && (
            <p style={{ color: "red", fontSize: "12px" }}>Preencha o campo</p>
          )}

          <ButtonModal
            backgroundColor="#77EC84"
            hoverBackgroundColor="#72E27F"
            onClick={() => {
              if (modalInputValue.trim() !== "") {
                handleInputChange(activeInputIndex, modalInputValue);
                setIsModalOpen(false);
              } else {
                setError(true);
              }
            }}
          >
            Salvar
          </ButtonModal>
        </ContainerModalInputs>
      </Modal>
    </>
  );
};

export default CustomPDFViewerSign;
